import { resolveTiPremiumPrice } from '../../calculatedAttributes/providers/tiQuote';
import type { PushDataLayerFn } from '../../types/pushDataLayer';
import type { TravelInsuranceQuotesData } from '../../types/travelInsurance';
import {
  ComponentTypeEnum,
  configureTiSearchResultParameters,
  getRewardCampaignName,
  productItem,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics';
import { getChooseOneOffers } from '../../utils/helpers/offers';
import type { JsonObject } from '../../utils/json';
import { resolveTrackingPrice } from '../PlanTierSelector/format';
import type { ComparePlan } from './types';

/**
 * GA tracking of the side-by-side comparison: the results page's compare checkbox and tray, and the
 * comparison page's table. One function per Airtable item; `component_title` and `intent` are A&A's
 * reporting keys and are sent verbatim.
 *
 * Every event fires from the handler of the control the reader used, never from a selection change:
 * removing a plan from the tray unchecks its card without the card reporting a click of its own.
 */

/** The results page's list, as the TI product card reports it. */
export const RESULTS_PAGE_ITEM_LIST_NAME = 'all_travel_insurance';

/**
 * `component_index` on the comparison page. The page is one block below the navigation bar — the
 * plan cards and the table under them — so everything on it reports row 0.
 */
export const COMPARE_PAGE_COMPONENT_INDEX = '0';

/** A&A's intent keys: sent verbatim, so each lives in one place. */
const INTENT = {
  add: 'Add to Compare',
  remove: 'Remove from Compare',
  compareNow: 'Compare Now',
} as const;

/** What the host app supplies; without it nothing is sent. */
export type CompareTracking = {
  pushDataLayer: PushDataLayerFn;
  /** The market's ISO currency code (SGD, HKD), as the host derives it for every TI price. */
  currencyCode: string;
  /**
   * The comparison page's list: its raw path, as the other product surfaces report theirs (the
   * `/-cms/draft` prefix is what tells preview traffic apart). Absent, the page's events carry no
   * `item_list_name` rather than a blank one; the results page's events name the card's list.
   */
  itemListName?: string;
  /** The trip the plans are priced for: the search parameter and the premiums. */
  travelQuotes?: TravelInsuranceQuotesData;
};

/** Where an event fires: the list it reports and the block's row on the page, where it has one. */
type Surface = {
  itemListName?: string;
  componentIndex?: string;
};

/**
 * A plan the event carries, with its position in the card list, the tray or the table. The
 * comparison page has the premium already; the results page resolves it from the quote.
 */
export type TrackedPlan = ComparePlan & {
  /** As the card's own events report it: a listing may index its cards as `row.column`. */
  index: number | string;
};

/**
 * A selected plan the tray has no product for - no card registered it and `loadProduct` has not
 * resolved it - so it is known by its id. Still counted: the event must carry every plan the reader
 * cleared or compared.
 */
export type TrackedId = { id: string; index: number };

export type TrayPlan = TrackedPlan | TrackedId;

const searchParameterOf = (tracking: CompareTracking) =>
  configureTiSearchResultParameters(tracking.travelQuotes);

/**
 * The compare events' own item fields, spread after the shared `productItem`. The price fields are
 * the TI apply event's (`resolveTrackingPrice`): `discount` only where the quote carries one. The
 * currency is the market's and is sent with or without a resolved price.
 */
const compareItemFields = (
  plan: TrackedPlan,
  tracking: CompareTracking,
): JsonObject => {
  const { product, premium } = plan;
  const price =
    premium ?? resolveTiPremiumPrice(product, tracking.travelQuotes?.premiums);
  const promotion = getRewardCampaignName(getChooseOneOffers(product));
  const searchParameter = searchParameterOf(tracking);
  const priceFields = resolveTrackingPrice(price, tracking.currencyCode);
  return {
    // A deal is a discounted premium, as the home insurance checkout reports it.
    deal_status: Number(priceFields.discount ?? 0) > 0,
    ...(promotion ? { promotion } : {}),
    currency: tracking.currencyCode,
    ...priceFields,
    ...(searchParameter ? { item_variant: searchParameter } : {}),
  };
};

/** The shared `items[]` entry of every product-bearing compare event. */
export function compareItem(
  plan: TrayPlan,
  tracking: CompareTracking,
): JsonObject {
  if (!('product' in plan)) {
    const searchParameter = searchParameterOf(tracking);
    return {
      item_id: plan.id,
      index: String(plan.index),
      currency: tracking.currencyCode,
      ...(searchParameter ? { item_variant: searchParameter } : {}),
    };
  }
  return {
    ...productItem(plan.product, String(plan.index)),
    ...compareItemFields(plan, tracking),
  };
}

/** The envelope of a `product_compare` event: the list, the search, the plans. */
const productCompare = (
  tracking: CompareTracking,
  surface: Surface,
  fields: {
    componentType: ComponentTypeEnum;
    componentTitle: string;
    action: UserActionEnum;
    intent: string;
    plans: TrayPlan[];
  },
) =>
  tracking.pushDataLayer({
    event: 'product_compare',
    component_type: fields.componentType,
    component_title: fields.componentTitle,
    ...(surface.componentIndex !== undefined
      ? { component_index: surface.componentIndex }
      : {}),
    action: fields.action,
    intent: fields.intent,
    search_result_parameter: searchParameterOf(tracking),
    ...(surface.itemListName ? { item_list_name: surface.itemListName } : {}),
    items: fields.plans.map((plan) => compareItem(plan, tracking)),
  });

const resultsPage = (componentIndex?: string): Surface => ({
  itemListName: RESULTS_PAGE_ITEM_LIST_NAME,
  componentIndex,
});

const comparePage = (tracking: CompareTracking): Surface => ({
  itemListName: tracking.itemListName,
  componentIndex: COMPARE_PAGE_COMPONENT_INDEX,
});

/** #2545: the compare checkbox on a product card, reporting the state it moved to. */
export const trackCardCheckbox = (
  tracking: CompareTracking,
  args: {
    /** The card listing's row on the page; `''` when the listing does not say. */
    componentIndex?: string;
    checked: boolean;
    plan: TrackedPlan;
  },
) =>
  productCompare(tracking, resultsPage(args.componentIndex ?? ''), {
    componentType: ComponentTypeEnum.CHECKBOX,
    componentTitle: 'Product Card/Checkbox_Compare',
    action: args.checked ? UserActionEnum.CHECK : UserActionEnum.UNCHECK,
    intent: args.checked ? INTENT.add : INTENT.remove,
    plans: [args.plan],
  });

// The tray's three events send no `component_index`: the tray floats over the page and sits in no
// block row, as the sticky footer CTA reports.

/** #2546: the × on a plan in the compare tray. */
export const trackTrayRemove = (
  tracking: CompareTracking,
  args: { plan: TrayPlan },
) =>
  productCompare(tracking, resultsPage(), {
    componentType: ComponentTypeEnum.BOTTOM_STICKY,
    componentTitle: 'Compare Tray/Button_Remove',
    action: UserActionEnum.CLICK,
    intent: INTENT.remove,
    plans: [args.plan],
  });

/** #2547: the tray's Clear, once for the whole selection. */
export const trackTrayClearAll = (
  tracking: CompareTracking,
  args: { clickText: string; plans: TrayPlan[] },
) =>
  productCompare(tracking, resultsPage(), {
    componentType: ComponentTypeEnum.BOTTOM_STICKY,
    componentTitle: `Compare Tray/Button_${args.clickText}`,
    action: UserActionEnum.CLICK,
    intent: INTENT.remove,
    plans: args.plans,
  });

/** #2548: the tray's compare CTA, with every plan being compared. */
export const trackTrayCompareNow = (
  tracking: CompareTracking,
  args: { clickText: string; plans: TrayPlan[] },
) =>
  productCompare(tracking, resultsPage(), {
    componentType: ComponentTypeEnum.BOTTOM_STICKY,
    componentTitle: `Compare Tray/CTA_${args.clickText}`,
    action: UserActionEnum.CLICK,
    intent: INTENT.compareNow,
    plans: args.plans,
  });

/** #2551: a plan's CTA in the comparison table, named by the label the reader clicked. */
export const trackTableCta = (
  tracking: CompareTracking,
  args: { clickText: string; plan: TrackedPlan },
) => {
  const surface = comparePage(tracking);
  sendProductAnalytics({
    event: 'product_click_apply',
    action: UserActionEnum.CLICK,
    componentType: ComponentTypeEnum.RESULT_PAGE_COMPARISON,
    componentTitle: `Product Comparison Table/CTA_${args.clickText}`,
    itemListName: surface.itemListName,
    product: args.plan.product,
    index: String(args.plan.index),
    pushDataLayer: tracking.pushDataLayer,
    extraData: {
      component_index: surface.componentIndex,
      search_result_parameter: searchParameterOf(tracking),
    },
    itemExtraData: compareItemFields(args.plan, tracking),
    omitProductContext: true,
  });
};

/** #2552: the empty "add plan" card in the table; `index` is the column clicked. */
export const trackTableAddPlan = (
  tracking: CompareTracking,
  args: { clickText: string; index: number },
) =>
  tracking.pushDataLayer({
    event: 'navigate',
    component_type: ComponentTypeEnum.BUTTON,
    component_title: `Product Comparison Table/Header_${args.clickText}`,
    component_index: COMPARE_PAGE_COMPONENT_INDEX,
    index: String(args.index),
    action: UserActionEnum.CLICK,
  });

/** #2553: the × on a plan's card in the table. */
export const trackTableRemove = (
  tracking: CompareTracking,
  args: { plan: TrackedPlan },
) =>
  productCompare(tracking, comparePage(tracking), {
    componentType: ComponentTypeEnum.RESULT_PAGE_COMPARISON,
    componentTitle: 'Product Comparison Table/Button_Remove',
    action: UserActionEnum.CLICK,
    intent: INTENT.remove,
    plans: [args.plan],
  });

/**
 * #2554: "Back to top". The page closes its offers section and its last section with one, so
 * `index` says which of them was clicked. `location` is inherited from the previous event and is
 * not sent.
 */
export const trackBackToTop = (
  tracking: CompareTracking,
  args: { clickText: string; index: number },
) =>
  tracking.pushDataLayer({
    event: 'navigate',
    component_type: ComponentTypeEnum.TEXT_LINK,
    component_title: `Product Comparison Table/Footer_${args.clickText}`,
    component_index: COMPARE_PAGE_COMPONENT_INDEX,
    action: UserActionEnum.CLICK,
    index: String(args.index),
  });
